export type PixAddressKeyType = "CPF" | "CNPJ" | "PHONE" | "EMAIL" | "EVP";

export interface PixValidationResult {
  isValid: boolean;
  normalizedKey: string;
  type: PixAddressKeyType;
  error?: string;
  typeMismatch?: boolean;
}

/**
 * Validação do algoritmo do Dígito Verificador do CPF (Módulo 11)
 */
export function isValidCpf(cpf: string): boolean {
  const clean = cpf.replace(/\D/g, "");
  if (clean.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(clean)) return false; // CPFs com todos os dígitos iguais

  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(clean.charAt(i), 10) * (10 - i);
  }
  let rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(clean.charAt(9), 10)) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(clean.charAt(i), 10) * (11 - i);
  }
  rev = 11 - (sum % 11);
  if (rev === 10 || rev === 11) rev = 0;
  if (rev !== parseInt(clean.charAt(10), 10)) return false;

  return true;
}

/**
 * Validação do algoritmo do Dígito Verificador do CNPJ (Módulo 11)
 */
export function isValidCnpj(cnpj: string): boolean {
  const clean = cnpj.replace(/\D/g, "");
  if (clean.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(clean)) return false;

  let size = clean.length - 2;
  let numbers = clean.substring(0, size);
  const digits = clean.substring(size);
  let sum = 0;
  let pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i), 10) * pos--;
    if (pos < 2) pos = 9;
  }
  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0), 10)) return false;

  size = size + 1;
  numbers = clean.substring(0, size);
  sum = 0;
  pos = size - 7;
  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i), 10) * pos--;
    if (pos < 2) pos = 9;
  }
  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1), 10)) return false;

  return true;
}

/**
 * Normaliza e valida telefone celular / fixo brasileiro para chave Pix.
 * Formato padrão esperado pelo Asaas e BACEN: +55 (DDD) (9 dígitos ou 8 dígitos).
 */
export function normalizeAndValidatePhone(phone: string): { isValid: boolean; normalizedKey: string; error?: string } {
  if (!phone) return { isValid: false, normalizedKey: "", error: "Telefone não informado." };
  let clean = phone.trim().replace(/[\s\-\(\)\.]/g, "");

  // Se já começar com +55, remove o + para checagem dos dígitos
  let hasPlus = clean.startsWith("+");
  if (hasPlus) clean = clean.substring(1);

  let digits = clean.replace(/\D/g, "");

  // Se começar com 55 e tiver 12 ou 13 dígitos (55 + DDD + 8 ou 9 dígitos)
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    digits = digits.substring(2);
  }

  // DDD no Brasil tem 2 dígitos entre 11 e 99
  if (digits.length !== 10 && digits.length !== 11) {
    return {
      isValid: false,
      normalizedKey: "",
      error: "O telefone informado deve conter DDD e 8 ou 9 dígitos."
    };
  }

  const ddd = parseInt(digits.substring(0, 2), 10);
  if (isNaN(ddd) || ddd < 11 || ddd > 99) {
    return {
      isValid: false,
      normalizedKey: "",
      error: "DDD inválido no telefone informado."
    };
  }

  // Formato Asaas para chave PHONE: dígitos incluindo DDD (10 ou 11 dígitos, sem prefixo +55 e sem máscara)
  const normalizedKey = digits;
  return { isValid: true, normalizedKey };
}

/**
 * Validação de e-mail para chave Pix
 */
export function isValidEmail(email: string): boolean {
  if (!email || email.length > 77) return false;
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
  return emailRegex.test(email.trim().toLowerCase());
}

/**
 * Validação de Chave Aleatória (EVP) - UUID v4 ou formato hexadecimal padrão BACEN
 */
export function isValidEvp(evp: string): boolean {
  if (!evp) return false;
  const clean = evp.trim();
  const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  const hex32Regex = /^[0-9a-fA-F]{32}$/;
  return uuidRegex.test(clean) || hex32Regex.test(clean);
}

/**
 * Validador e normalizador mestre de Chave Pix conforme o tipo explicitamente informado pelo usuário.
 */
export function validatePixKey(key: string, type: PixAddressKeyType | string): PixValidationResult {
  const rawKey = (key || "").trim();
  const normalizedType = normalizePixKeyType(type);

  if (!rawKey) {
    return {
      isValid: false,
      normalizedKey: "",
      type: normalizedType,
      error: "Chave Pix inválida. Confira o tipo de chave e o valor informado."
    };
  }

  switch (normalizedType) {
    case "CPF": {
      // Se tiver @ ou letras (fora formatação de CPF), é incompatibilidade de tipo
      if (rawKey.includes("@") || /[a-zA-Z]/.test(rawKey)) {
        return {
          isValid: false,
          normalizedKey: rawKey,
          type: "CPF",
          typeMismatch: true,
          error: "Os dados informados não correspondem ao tipo de chave selecionado."
        };
      }
      const digits = rawKey.replace(/\D/g, "");
      if (digits.length !== 11 || !isValidCpf(digits)) {
        return {
          isValid: false,
          normalizedKey: digits,
          type: "CPF",
          error: "Chave Pix inválida. Confira o tipo de chave e o valor informado."
        };
      }
      return {
        isValid: true,
        normalizedKey: digits,
        type: "CPF"
      };
    }

    case "CNPJ": {
      if (rawKey.includes("@") || /[a-zA-Z]/.test(rawKey)) {
        return {
          isValid: false,
          normalizedKey: rawKey,
          type: "CNPJ",
          typeMismatch: true,
          error: "Os dados informados não correspondem ao tipo de chave selecionado."
        };
      }
      const digits = rawKey.replace(/\D/g, "");
      if (digits.length !== 14 || !isValidCnpj(digits)) {
        return {
          isValid: false,
          normalizedKey: digits,
          type: "CNPJ",
          error: "Chave Pix inválida. Confira o tipo de chave e o valor informado."
        };
      }
      return {
        isValid: true,
        normalizedKey: digits,
        type: "CNPJ"
      };
    }

    case "PHONE": {
      if (rawKey.includes("@")) {
        return {
          isValid: false,
          normalizedKey: rawKey,
          type: "PHONE",
          typeMismatch: true,
          error: "Os dados informados não correspondem ao tipo de chave selecionado."
        };
      }
      const phoneRes = normalizeAndValidatePhone(rawKey);
      if (!phoneRes.isValid) {
        return {
          isValid: false,
          normalizedKey: rawKey,
          type: "PHONE",
          error: "Chave Pix inválida. Confira o tipo de chave e o valor informado."
        };
      }
      return {
        isValid: true,
        normalizedKey: phoneRes.normalizedKey,
        type: "PHONE"
      };
    }

    case "EMAIL": {
      if (!rawKey.includes("@")) {
        return {
          isValid: false,
          normalizedKey: rawKey,
          type: "EMAIL",
          typeMismatch: true,
          error: "Os dados informados não correspondem ao tipo de chave selecionado."
        };
      }
      const cleanEmail = rawKey.toLowerCase();
      if (!isValidEmail(cleanEmail)) {
        return {
          isValid: false,
          normalizedKey: cleanEmail,
          type: "EMAIL",
          error: "Chave Pix inválida. Confira o tipo de chave e o valor informado."
        };
      }
      return {
        isValid: true,
        normalizedKey: cleanEmail,
        type: "EMAIL"
      };
    }

    case "EVP": {
      if (rawKey.includes("@")) {
        return {
          isValid: false,
          normalizedKey: rawKey,
          type: "EVP",
          typeMismatch: true,
          error: "Os dados informados não correspondem ao tipo de chave selecionado."
        };
      }
      const cleanEvp = rawKey.toLowerCase();
      if (!isValidEvp(cleanEvp)) {
        return {
          isValid: false,
          normalizedKey: cleanEvp,
          type: "EVP",
          error: "Chave Pix inválida. Confira o tipo de chave e o valor informado."
        };
      }
      return {
        isValid: true,
        normalizedKey: cleanEvp,
        type: "EVP"
      };
    }

    default:
      return {
        isValid: false,
        normalizedKey: rawKey,
        type: "EVP",
        error: "Chave Pix inválida. Confira o tipo de chave e o valor informado."
      };
  }
}

/**
 * Normaliza a string do tipo de chave Pix para um dos valores válidos do enum PixAddressKeyType.
 */
export function normalizePixKeyType(type?: string): PixAddressKeyType {
  if (!type) return "EVP";
  const upper = String(type).trim().toUpperCase();
  if (upper === "CPF") return "CPF";
  if (upper === "CNPJ") return "CNPJ";
  if (upper === "PHONE" || upper === "TELEFONE" || upper === "CELULAR") return "PHONE";
  if (upper === "EMAIL" || upper === "E-MAIL") return "EMAIL";
  if (upper === "EVP" || upper === "RANDOM" || upper === "ALEATORIA" || upper === "CHAVE_ALEATORIA") return "EVP";
  return "EVP";
}

/**
 * Mascara uma chave Pix de maneira segura para exibição em logs e interfaces sem expor dados sensíveis.
 */
export function maskPixKeyDisplay(pixKey?: string, type?: PixAddressKeyType | string): string {
  if (!pixKey || typeof pixKey !== "string") return "Chave Pix não cadastrada";
  const clean = pixKey.trim();
  if (clean.length <= 4) return "****";

  const normType = normalizePixKeyType(type);

  if (normType === "EMAIL" && clean.includes("@")) {
    const [user, domain] = clean.split("@");
    const maskedUser = user.length > 2 ? user.substring(0, 2) + "***" : "***";
    return `${maskedUser}@${domain}`;
  }

  if (normType === "PHONE") {
    // Ex: +5561999994186 -> +55 61 ****-4186
    const digits = clean.replace(/\D/g, "");
    if (digits.length >= 10) {
      const ddd = digits.startsWith("55") ? digits.substring(2, 4) : digits.substring(0, 2);
      const last4 = digits.substring(digits.length - 4);
      return `(${ddd}) *****-${last4}`;
    }
  }

  if (normType === "CPF") {
    const digits = clean.replace(/\D/g, "");
    if (digits.length === 11) {
      return `${digits.substring(0, 3)}.***.***-${digits.substring(9)}`;
    }
  }

  if (normType === "CNPJ") {
    const digits = clean.replace(/\D/g, "");
    if (digits.length === 14) {
      return `${digits.substring(0, 2)}.***.***/${digits.substring(8, 12)}-${digits.substring(12)}`;
    }
  }

  // Fallback / EVP
  return `${clean.substring(0, 4)}...${clean.substring(clean.length - 4)}`;
}
